import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils";
import { capitalize } from "utils";

import { useSnackbar } from "contexts";
import { useReferrerState } from "hooks";
import {
  useProductMutations,
  useAddProductsFromIntegration,
  useShopifyConnectionStatus,
  useImportProductFromUrl,
} from "hooks/api";
import {
  PlusIcon,
  UploadIcon,
  StoreIcon,
  PencilIcon,
  ChevronRightIcon,
  LinkIcon,
} from "lucide-react";
import { ROUTES } from "routes";

import { Box, Stack, Typography, Collapse, useTheme } from "@mui/material";

import { INTEGRATION_STATUSES } from "constants/statuses";

import { Alert, Button, Image, Input, Modal } from "@repo/ui";
import { ImportModal, SearchSelect, SelectField, StatusChip } from "components";
import type { ImportProductFromUrlResponse } from "services";

import {
  ECOMMERCE_INTEGRATIONS,
  DEFAULT_VISIBLE_INTEGRATIONS,
  MethodOption,
  BackRow,
  IntegrationRow,
  type EcommerceIntegration,
} from "./shared";

// Which screen of the Add modal is showing.
//  - hub:         the method chooser (Connect a store / Add manually)
//  - integration: browse + select a connected store's products
//  - manual:      Create a product / Paste a listing URL / Import a file
//  - url:         paste a listing URL, then review in the product form
type AddMode = "hub" | "integration" | "manual" | "url";

interface AddModalProps {
  company: any;
  source?: string;
  items?: Array<{
    id: string;
    title: string;
    sku: string | null;
    description: string;
    price: number;
    images: string[];
    created_at: string;
    updated_at: string;
    imported?: boolean;
    category_id?: string | null;
    product_url?: string | null;
    integration?: { name: string } & Partial<{
      id: number;
      name: string;
      logo: string;
      categories: string[];
      summary: string;
      description: string;
      instructions: string;
      default_field_mapping: Record<string, any>;
    }>;
  }>;
  open: boolean;
  createRoute: string;
  searchParams?: {
    fields: string[];
    preview: string;
  };
  onClose: () => void;
  refetch: () => void;
}

export const AddModal = (props: AddModalProps) => {
  const {
    company,
    source,
    items = [],
    open,
    createRoute,
    searchParams = { fields: [], preview: "" },
    onClose = () => undefined,
    refetch,
  } = props;
  const theme = useTheme();
  const navigate = useNavigate();
  const referrer = useReferrerState();
  const searchSelectRef = useRef<any>(null);

  type Item = NonNullable<AddModalProps["items"]>[number];
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [mode, setMode] = useState<AddMode>("hub");
  const [showAllIntegrations, setShowAllIntegrations] = useState(false);
  // "Add integrations" (from the connected-store import view) forces the connect-a-store
  // catalog back on-screen so the user can connect another integration WITHOUT leaving
  // the modal. Reset when leaving integration mode / closing.
  const [showConnectCatalog, setShowConnectCatalog] = useState(false);
  // VRG-353 — paste-a-listing-URL step.
  const [importUrl, setImportUrl] = useState("");
  const [importError, setImportError] = useState<string | null>(null);
  // Held back when the URL matches a product this company already has, so the
  // duplicate can be shown BEFORE the form rather than as a SKU-constraint
  // error after the user has filled one in.
  const [pendingImport, setPendingImport] = useState<ImportProductFromUrlResponse | null>(
    null,
  );

  // Most popular first; show the top few, reveal the rest with "View N more".
  const sortedIntegrations = [...ECOMMERCE_INTEGRATIONS].sort((a, b) => b.installs - a.installs);
  const defaultIntegrations = sortedIntegrations.slice(0, DEFAULT_VISIBLE_INTEGRATIONS);
  const extraIntegrations = sortedIntegrations.slice(DEFAULT_VISIBLE_INTEGRATIONS);
  const hasMoreIntegrations = extraIntegrations.length > 0;

  const renderIntegrationRow = (integration: EcommerceIntegration) => (
    <IntegrationRow
      key={integration.id}
      integration={integration}
      connected={isIntegrationConnected(integration.name)}
      onClick={() => {
        handleClose();
        // Products -> an integration's setup page is a cross-surface jump: the user came
        // here to add products and will want to come back to the list they left.
        navigate(ROUTES.SETTINGS_COMPANY_INTEGRATION(integration.id), { state: referrer });
      }}
    />
  );

  // Connection status drives the "Connect a store" view: no connected store →
  // show the integrations catalog; connected → show its importable products.
  const { data: shopifyStatus } = useShopifyConnectionStatus();
  const isIntegrationConnected = (name: string) =>
    name.toLowerCase() === "shopify" ? shopifyStatus?.connected === true : false;
  const hasConnectedStore = ECOMMERCE_INTEGRATIONS.some((i) =>
    isIntegrationConnected(i.name),
  );

  // Product-source integrations that are currently connected (today: Shopify).
  // Items carry their own `integration` when available; otherwise fall back to the
  // connected source so every imported product shows where it came from (point 3).
  const connectedSources = ECOMMERCE_INTEGRATIONS.filter((i) => isIntegrationConnected(i.name));
  const resolveItemSource = (item: any): { name: string; logo?: string } | null => {
    if (item?.integration?.name) return item.integration as { name: string; logo?: string };

    // NO fallback to the first connected store. It used to return
    // `connectedSources[0]`, on the assumption that everything in this list came
    // from an integration feed — which stopped being true once products could be
    // created manually or from a pasted URL. A URL-imported product was then
    // badged "Shopify", which is not a cosmetic slip: the badge is the only
    // thing telling a user where a record came from, and provenance is the
    // product. Better no badge than a wrong one.
    if (item?.integration_provider) {
      return {name: item.integration_provider};
    }
    return null;
  };

  // Source filter (point 6): null = all sources (default). Toggling in the dropdown
  // makes it an explicit list. `effectiveSources` resolves null → all.
  const [selectedSources, setSelectedSources] = useState<string[] | null>(null);
  const sourceNames = connectedSources.map((s) => s.name);
  const effectiveSources = selectedSources ?? sourceNames;
  const visibleItems = items.filter((it) => {
    const src = resolveItemSource(it);
    return src ? effectiveSources.includes(src.name) : true;
  });
  // Trigger label: "All integrations" when all selected, the name when exactly one,
  // a count when a subset — never a static label + generic logo.
  const renderSourcesLabel = () => {
    if (connectedSources.length <= 1) return connectedSources[0]?.name ?? "Integrations";
    if (effectiveSources.length === connectedSources.length) return "All integrations";
    if (effectiveSources.length === 0) return "No integrations";
    if (effectiveSources.length === 1) return effectiveSources[0];
    return `${effectiveSources.length} integrations`;
  };

  const { open: notification } = useSnackbar();

  const [, { isLoading }] = useProductMutations({
    onSuccess: () => {
      notification({
        type: "success",
        title: `${selectedItems.length} ${source} have been succcessfully created`,
      });
      handleClose();
      refetch();
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      notification({
        type: "error",
        title: message,
      });
    },
  });

  const {
    addProductsFromIntegration,
    isLoading: isLoadingFromIntegration,
    data: bulkData,
    reset: resetBulkMutation
  } = useAddProductsFromIntegration({
    onSuccess: (data) => {
      const { totalSuccessful, totalFailed, totalAttempted } = data;

      if (totalFailed > 0) {
        // Show partial success with option to view details
        setShowResults(true);
        notification({
          type: "warning",
          title: `Import partially completed: ${totalSuccessful}/${totalAttempted} products added`,
          text: "Some products failed to import. Click to view details.",
          action: (
            <Button variant="text" onClick={() => setShowResults(true)}>
              View Details
            </Button>
          )
        });
      } else {
        // Complete success
        handleClose();
        refetch();
      }
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      notification({
        type: "error",
        title: `Failed to add products from ${capitalize(source || "")}: ${message}`,
      });
    },
  });

  const handleCreate = () => {
    if (!company || selectedItems.length === 0) return;

    // Filter out any imported products from selected items
    const nonImportedItems = selectedItems.filter(item => !item.imported);

    if (nonImportedItems.length === 0) {
      notification({
        type: "warning",
        title: "Selected products are already imported",
        text: "Please select products that haven't been imported yet.",
      });
      return;
    }

    const firstItem = nonImportedItems[0];
    let provider = (source || '').toLowerCase();

    if (firstItem?.integration?.name) {
      provider = firstItem.integration.name.toLowerCase();
    } else if (typeof firstItem?.id === 'string' && firstItem.id.toLowerCase().includes('shopify')) {
      provider = 'shopify';
    } else if (provider && ['shopify', 'shopify products', 'products'].includes(provider)) {
      // "products" here refers to Shopify products selection context in the UI
      provider = 'shopify';
    }

    if (!provider) {
      notification({
        type: "error",
        title: "Unable to determine integration provider",
      });
      return;
    }

    // Always use bulk operation (handles single products as well)
    const integrationProducts = nonImportedItems.map((item) => ({
      id: item.id,
      title: item.title,
      sku: item.sku ?? undefined,
      description: item.description || "",
      price: item.price || 0,
      images: item.images || [],
      category_id: item.category_id ?? undefined,
      // This map is an ALLOWLIST, not a spread: a field missing here never reaches the
      // API, with no error and a 200 response. `product_url` was absent, which is half
      // of why integration products landed with a null storefront link.
      product_url: item.product_url ?? undefined,
      created_at: new Date(item.created_at),
      updated_at: new Date(item.updated_at),
    }));

    addProductsFromIntegration({
      provider,
      integration_products: integrationProducts,
    });
  };

  const handleImport = () => {
    setOpenImportModal(false);
  };

  const handleClear = () => {
    setSelectedItems([]);
    searchSelectRef.current?.handleClear();
  };

  // VRG-353 — read a listing URL, then hand the values to the product form for
  // review. Nothing is created here: the API extracts, the user confirms, and
  // the form's own Save is what writes the product.
  const { importFromUrlAsync, isLoading: isImporting, reset: resetImport } =
    useImportProductFromUrl();

  const handleClose = () => {
    setSelectedItems([]);
    setShowResults(false);
    setOpenImportModal(false);
    setMode("hub");
    setShowAllIntegrations(false);
    setShowConnectCatalog(false);
    setImportUrl("");
    setImportError(null);
    setPendingImport(null);
    resetImport();
    resetBulkMutation();
    onClose();
  };

  // The referrer must be SPREAD, not replaced — the shared Header reads
  // backTo/backLabel off the same location.state to render its back-link.
  const openFormWith = (result: ImportProductFromUrlResponse) => {
    navigate(createRoute, { state: { ...referrer, importedProduct: result } });
    handleClose();
  };

  const handleImportFromUrl = async () => {
    const url = importUrl.trim();
    if (!url) return;

    setImportError(null);
    try {
      const result = await importFromUrlAsync(url);

      // Stop here on a duplicate so the user can open what they already have.
      // Letting them through would mean filling in a whole form only to hit the
      // per-company SKU uniqueness constraint at save.
      if (result.duplicate) {
        setPendingImport(result);
        return;
      }

      openFormWith(result);
    } catch (error) {
      // Kept inline rather than a toast: the input that caused it is on screen
      // and the user needs to correct it in place.
      setImportError(getErrorMessage(error) || "Could not import from that URL.");
    }
  };

  const handleSecondaryAction = (item: any) => {
    // Show the SOURCE integration the product came from (logo + name), not its price
    // — in an import context the source is what matters, and with multiple
    // integrations connected it's how you tell which products came from where (point 3).
    const src = resolveItemSource(item);
    if (!src) return null;
    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          border: `1px solid ${theme.palette.grey[100]}`,
          borderRadius: 1.5,
          // Match the data-table status chips' padding/height (StatusChip small: 12px
          // horizontal, ~28px tall) — was cramped at px 10 / py 4.
          px: 1.5,
          py: 0.75,
          minHeight: 28,
          flexShrink: 0,
        }}
      >
        {src.logo && (
          <Image
            src={src.logo}
            alt={src.name}
            fit="cover"
            sx={{ borderRadius: 0.75, minWidth: 16, maxWidth: 16, height: 16 }}
          />
        )}
        <Typography variant="body4" color="text.secondary" noWrap>
          {src.name}
        </Typography>
      </Stack>
    );
  };

  // The Add primary only belongs to the integration browse view (where items are
  // selected). The hub + manual screens navigate/branch on row click, so they
  // carry no footer.
  const showIntegrationActions =
    mode === "integration" && !openImportModal && hasConnectedStore && items.length > 0;

  return (
    <Modal
      title={`Add ${source}`}
      open={open}
      onClose={handleClose}
      // The integration import view is a scroll LIST, not a form — the Modal's generous
      // bottom padding (pb:7) leaves a dead band between the last row and the footer.
      // Trim it here so the list butts close to the "Add Products" bar. Other views (the
      // method-chooser rows) keep the default breathing room.
      bodySx={showIntegrationActions ? { pb: 3 } : undefined}
      actions={
        showIntegrationActions ? (
          <Stack direction="row" sx={{ minWidth: "100%" }} justifyContent="flex-end">
            <Button
              disableElevation
              onClick={() => handleCreate()}
              disabled={
                selectedItems.length == 0 ||
                selectedItems.filter(item => !item.imported).length === 0 ||
                isLoading ||
                isLoadingFromIntegration
              }
            >
              Add {source ? capitalize(source) : "Item"}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {/* HUB — choose how to add */}
      {mode === "hub" && !openImportModal && (
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            How would you like to add {source}?
          </Typography>
          {/* VERA-455 — the row states the CONNECTION, it does not ask for one that already
              exists. It read "Connect a store · Sync products automatically from Shopify and
              other integrations" to an operator whose Shopify has been connected for months,
              which says the opposite of the truth and sends them off to re-do setup. The
              destination was already correct in both states (`setMode("integration")` shows the
              connected store's importable products and falls back to the catalog when there is
              nothing connected) — only the label was blind to it.

              Named after the store rather than the act, because with a store connected the
              operator is not choosing a method any more, they are choosing WHICH source, and the
              name plus its logo is what tells them the right one is already there. */}
          {hasConnectedStore ? (
            <MethodOption
              icon={
                connectedSources.length === 1 && connectedSources[0].logo ? (
                  <Image
                    src={connectedSources[0].logo}
                    alt={connectedSources[0].name}
                    fit="cover"
                    sx={{ borderRadius: 0.75, minWidth: 24, maxWidth: 24, height: 24 }}
                  />
                ) : (
                  <StoreIcon strokeWidth={1} size={20} />
                )
              }
              title={connectedSources.length === 1 ? connectedSources[0].name : "Your connected stores"}
              description="Import products from your connected store."
              trailing={
                // The SAME chip `IntegrationRow` puts on a connected catalog row, so the word
                // and its tone match wherever a connection is stated (§28/§36).
                <StatusChip statuses={INTEGRATION_STATUSES} status="connected" size="small" />
              }
              onClick={() => setMode("integration")}
            />
          ) : (
            <MethodOption
              icon={<StoreIcon strokeWidth={1} size={20} />}
              title="Connect a store"
              description="Sync products automatically from Shopify and other integrations."
              onClick={() => setMode("integration")}
            />
          )}
          <MethodOption
            icon={<PencilIcon strokeWidth={1} size={20} />}
            title="Add manually"
            description="Create a product yourself, paste a link to one, or import a file."
            onClick={() => setMode("manual")}
          />
        </Stack>
      )}

      {/* MANUAL — create one, paste a listing URL, or import a file */}
      {mode === "manual" && !openImportModal && (
        <Stack spacing={3}>
          <BackRow onBack={() => setMode("hub")} />
          <MethodOption
            icon={<PlusIcon strokeWidth={1} size={20} />}
            title="Create a product"
            description="Add one product using the full product form."
            onClick={() => navigate(createRoute)}
          />
          <MethodOption
            icon={<LinkIcon strokeWidth={1} size={20} />}
            title="Paste a product link"
            description="Fill the form from a product page on your own site."
            onClick={() => setMode("url")}
          />
          <MethodOption
            icon={<UploadIcon strokeWidth={1} size={20} />}
            title="Import a file"
            description="Bulk-add products from a CSV file."
            onClick={() => setOpenImportModal(true)}
          />
        </Stack>
      )}

      {/* URL — VRG-353: duplicate found, offer the existing product first */}
      {mode === "url" && pendingImport?.duplicate && (
        <Stack spacing={3}>
          <BackRow
            onBack={() => {
              setPendingImport(null);
              setImportError(null);
            }}
          />
          <Typography variant="body2" color="text.secondary">
            You already have a product that matches this link
            {pendingImport.duplicate.matchedOn === "sku"
              ? ` (same SKU, ${pendingImport.duplicate.sku})`
              : ""}
            .
          </Typography>
          <Box
            sx={{
              border: `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
              p: 4,
            }}
          >
            <Typography variant="body2" fontWeight={500}>
              {pendingImport.duplicate.title}
            </Typography>
            <Typography variant="body3" color="text.secondary">
              SKU {pendingImport.duplicate.sku}
            </Typography>
          </Box>
          {pendingImport.duplicate.syncOwned && (
            <Alert
              severity="warning"
              // A sync-owned product is not merely a duplicate: anything typed
              // into a second copy is overwritten by the next provider sync, so
              // this is worth more than the neutral wording above.
              description="This product is kept in sync with your connected store. Changes you make to a copy will be overwritten the next time it syncs."
            />
          )}
          <Stack direction="row" spacing={3} justifyContent="flex-end">
            <Button
              variant="secondary"
              onClick={() => {
                const existingId = pendingImport.duplicate!.id;
                handleClose();
                navigate(ROUTES.PRODUCT(existingId), { state: referrer });
              }}
            >
              Open existing
            </Button>
            <Button variant="primary" onClick={() => openFormWith(pendingImport)}>
              Add anyway
            </Button>
          </Stack>
        </Stack>
      )}

      {/* URL — VRG-353: paste a listing URL, review the result in the form */}
      {mode === "url" && !pendingImport?.duplicate && (
        <Stack spacing={3}>
          <BackRow
            onBack={() => {
              setMode("manual");
              setImportError(null);
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Paste the link to a product page on your site. We&apos;ll read what we can and
            fill in the form. You can check and change everything before saving.
          </Typography>
          {/* An Alert rather than the field's helper text. These failures are
              outcomes, not input mistakes: "that site blocks automated access"
              is three lines of explanation plus a way forward, which a single
              red line under the input cannot carry and which the user would
              read as "you typed the URL wrong". The field still shows its error
              border so the pairing is obvious. */}
          {importError && <Alert severity="error" description={importError} />}
          <Input
            label="Product page link"
            placeholder="https://your-store.com/products/example"
            value={importUrl}
            // This Input hands back the VALUE, not the change event.
            onChange={(value: string) => {
              setImportUrl(value);
              if (importError) setImportError(null);
            }}
            handleKeyDown={(event) => {
              if (event.key === "Enter" && !isImporting) handleImportFromUrl();
            }}
            // Error BORDER only — the message itself is in the Alert above, so
            // repeating it here would print the same sentence twice.
            error={Boolean(importError)}
            disabled={isImporting}
          />
          <Stack direction="row" spacing={3} justifyContent="flex-end">
            <Button
              variant="secondary"
              onClick={() => {
                setMode("manual");
                setImportError(null);
              }}
              disabled={isImporting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleImportFromUrl}
              disabled={!importUrl.trim() || isImporting}
              loading={isImporting}
            >
              {isImporting ? "Reading page" : "Continue"}
            </Button>
          </Stack>
        </Stack>
      )}

      {/* INTEGRATION — browse + select a connected store's products */}
      {mode === "integration" && !openImportModal && (
        <Stack spacing={3}>
          <BackRow
            onBack={() => {
              // If the catalog was opened via "Add integrations" (store already
              // connected), back returns to the import view; otherwise back to the hub.
              if (showConnectCatalog) {
                setShowConnectCatalog(false);
                return;
              }
              handleClear();
              setShowResults(false);
              setMode("hub");
            }}
          />

          {(!hasConnectedStore || showConnectCatalog) ? (
            // No store connected yet (or "Add integrations" was clicked) — show the
            // most-popular integrations to connect one; "View more" reveals the rest.
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Connect a store to import its products automatically.
              </Typography>
              <Stack spacing={2}>
                {defaultIntegrations.map(renderIntegrationRow)}
              </Stack>
              {hasMoreIntegrations && (
                // No inner scroll — the whole list flows and the modal body scrolls as
                // one unit (the top rows scroll away rather than staying pinned).
                <Collapse in={showAllIntegrations} timeout={300}>
                  <Stack spacing={2}>
                    {extraIntegrations.map(renderIntegrationRow)}
                  </Stack>
                </Collapse>
              )}
              {hasMoreIntegrations && (
                <Button
                  variant="tertiary"
                  disableElevation
                  onClick={() => setShowAllIntegrations((prev) => !prev)}
                  endIcon={
                    <ChevronRightIcon
                      strokeWidth={1.5}
                      size={16}
                      style={{
                        transform: showAllIntegrations ? "rotate(-90deg)" : "rotate(90deg)",
                        transition: "transform .2s",
                      }}
                    />
                  }
                  sx={{ alignSelf: "flex-start" }}
                >
                  {showAllIntegrations ? "View less" : `View ${extraIntegrations.length} more`}
                </Button>
              )}
            </Stack>
          ) : items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              No products to import from your connected store yet.
            </Typography>
          ) : (
            <>
              <SearchSelect
                items={visibleItems}
                ref={searchSelectRef}
                onChange={(items: any) => setSelectedItems(items)}
                showClear={false}
                listItemSecondaryAction={handleSecondaryAction}
                searchParams={searchParams as any}
                searchButton={
                  // Integrations dropdown (point 6) via the shared SelectField multi-select
                  // (the polished multi-select built on VERA-405 — right-aligned checks,
                  // Select-all/Clear-all): the connected product sources, all selected by
                  // default, double as a source filter for the list. "Add integrations"
                  // lives in the menu footer.
                  // Narrower than the old 220px (which overlapped the search field on the
                  // row) — fits "All integrations" without crowding the search.
                  <Box sx={{ minWidth: 170, flexShrink: 0 }}>
                    <SelectField
                      multiple
                      menuMatchTrigger
                      value={effectiveSources}
                      onChange={(v) => setSelectedSources(v as string[])}
                      options={connectedSources.map((s) => ({
                        label: s.name,
                        value: s.name,
                        icon: s.logo ? (
                          <Image src={s.logo} alt={s.name} fit="cover" sx={{ borderRadius: 1, minWidth: 20, maxWidth: 20 }} />
                        ) : undefined,
                      }))}
                      renderValue={() => renderSourcesLabel()}
                      menuFooter={
                        // Plain text button, matching the "Add Sort" affordance in the
                        // table Sort dropdown — no top border / box-shadow. The PlusIcon
                        // gets boxShadow/border cleared (the @repo/ui Button icon slot's
                        // default outline was the "weird gradient" here). Stay in the modal:
                        // reveal the connect-a-store catalog step.
                        <Button
                          disableElevation
                          variant="text"
                          size="large"
                          color="inherit"
                          onClick={() => setShowConnectCatalog(true)}
                          startIcon={
                            <PlusIcon
                              strokeWidth={1}
                              style={{ boxShadow: "none", border: "none" }}
                            />
                          }
                          // mt gives a gap from the option rows above (the borderTop
                          // divider was removed; this keeps them from abutting).
                          sx={{ width: "100%", justifyContent: "left", fontSize: "16px", mt: 1 }}
                        >
                          Add integrations
                        </Button>
                      }
                    />
                  </Box>
                }
              />

              {/* Results display */}
              {showResults && bulkData && (
                <Stack spacing={2} p={2} sx={{ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={500}>
                      Import Results ({bulkData.totalSuccessful}/{bulkData.totalAttempted})
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => {
                        setShowResults(false);
                        handleClose();
                        refetch();
                      }}
                    >
                      Continue
                    </Button>
                  </Stack>

                  <Stack spacing={1} maxHeight={200} sx={{ overflowY: 'auto' }}>
                    {bulkData.results.map((result) => (
                      <Stack key={result.integrationProductId} direction="row" spacing={1} alignItems="center">
                        <Typography
                          variant="body3"
                          color={result.success ? 'success.main' : 'error.main'}
                          sx={{ minWidth: 60 }}
                        >
                          {result.success ? '✓ Success' : '✗ Failed'}
                        </Typography>
                        <Typography variant="body3" sx={{ flex: 1 }}>
                          {selectedItems.find(item => item.id === result.integrationProductId)?.title || result.integrationProductId}
                        </Typography>
                        {!result.success && (
                          <Typography variant="body3" color="error.main" sx={{ fontSize: '0.75rem' }}>
                            {result.error}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}

      {openImportModal && (
        <ImportModal
          source={source || ""}
          open={true}
          onClose={() => setOpenImportModal(false)}
          refetch={refetch}
          onImport={() => {
            handleImport()
            handleClose()
          }}
        />
      )}
    </Modal>
  );
};
